import { useMemo, useState } from 'react';
import { MONTH_NAMES } from '../utils/scheduler';

function aggregate(schedule, persons, facilitators, prayers, ushers) {
  const counts = {};

  for (const p of persons) {
    const allRoles = [
      ...facilitators.filter(f => f.personId === p.id),
      ...prayers.filter(pr => pr.personId === p.id),
      ...ushers.filter(u => u.personId === p.id),
    ];
    const hasActiveRole = allRoles.some(r => r.active !== false);
    counts[p.id] = { name: p.name, fac: 0, pray: 0, usher: 0, resting: !hasActiveRole && allRoles.length > 0 };
  }

  for (const s of schedule) {
    [s.noonFacilitator, s.eveningFacilitator].forEach(p => {
      if (!p) return;
      if (!counts[p.id]) counts[p.id] = { name: p.name, fac: 0, pray: 0, usher: 0, resting: false };
      counts[p.id].fac++;
    });
    [s.noonPrayer, s.eveningPrayer].forEach(p => {
      if (!p) return;
      if (!counts[p.id]) counts[p.id] = { name: p.name, fac: 0, pray: 0, usher: 0, resting: false };
      counts[p.id].pray++;
    });
    s.ushers.forEach(p => {
      if (!counts[p.id]) counts[p.id] = { name: p.name, fac: 0, pray: 0, usher: 0, resting: false };
      counts[p.id].usher++;
    });
  }
  return Object.values(counts);
}

function minMax(rows, key) {
  const vals = rows.filter(r => !r.resting && r[key] > 0).map(r => r[key]);
  if (vals.length < 2) return { min: -1, max: -1 };
  return { min: Math.min(...vals), max: Math.max(...vals) };
}

function CountCell({ value, min, max, resting }) {
  if (value === 0) return <td style={{ textAlign: 'center', color: '#cbd5e1' }}>—</td>;
  const isMin = !resting && value === min && min !== max;
  const isMax = !resting && value === max && min !== max;
  return (
    <td style={{
      textAlign: 'center', fontWeight: 700,
      color: isMax ? '#dc2626' : isMin ? '#059669' : resting ? '#94a3b8' : 'var(--text)',
      background: isMax ? '#fef2f2' : isMin ? '#f0fdf4' : 'transparent',
    }}>
      {value}
      {isMax && <span style={{ fontSize: '0.65rem', marginLeft: 3 }}>▲</span>}
      {isMin && <span style={{ fontSize: '0.65rem', marginLeft: 3 }}>▼</span>}
    </td>
  );
}

// 빠른 범위 프리셋
const PRESETS = [
  { label: '연간', startMonth: 0, endMonth: 11 },
  { label: '1분기', startMonth: 0, endMonth: 2 },
  { label: '2분기', startMonth: 3, endMonth: 5 },
  { label: '3분기', startMonth: 6, endMonth: 8 },
  { label: '4분기', startMonth: 9, endMonth: 11 },
  { label: '상반기', startMonth: 0, endMonth: 5 },
  { label: '하반기', startMonth: 6, endMonth: 11 },
];

export default function StatsView({ schedule, year, persons, facilitators, prayers, ushers }) {
  const [startMonth, setStartMonth] = useState(0);
  const [endMonth,   setEndMonth]   = useState(11);
  const [showResting, setShowResting] = useState(true);

  function applyPreset(p) {
    setStartMonth(p.startMonth);
    setEndMonth(p.endMonth);
  }

  function handleStartChange(val) {
    const v = Number(val);
    setStartMonth(v);
    if (v > endMonth) setEndMonth(v);
  }

  function handleEndChange(val) {
    const v = Number(val);
    setEndMonth(v);
    if (v < startMonth) setStartMonth(v);
  }

  const isPreset = p => p.startMonth === startMonth && p.endMonth === endMonth;

  const rangeLabel = startMonth === endMonth
    ? MONTH_NAMES[startMonth]
    : `${MONTH_NAMES[startMonth]} ~ ${MONTH_NAMES[endMonth]}`;

  const filtered = useMemo(() =>
    schedule.filter(s => { const m = s.date.getMonth(); return m >= startMonth && m <= endMonth; }),
    [schedule, startMonth, endMonth]
  );

  const allRows  = useMemo(() => aggregate(filtered, persons, facilitators, prayers, ushers), [filtered, persons, facilitators, prayers, ushers]);
  const rows     = useMemo(() => showResting ? allRows : allRows.filter(r => !r.resting), [allRows, showResting]);

  const activeRows   = allRows.filter(r => !r.resting);
  const facMM   = useMemo(() => minMax(activeRows, 'fac'),   [activeRows]);
  const prayMM  = useMemo(() => minMax(activeRows, 'pray'),  [activeRows]);
  const usherMM = useMemo(() => minMax(activeRows, 'usher'), [activeRows]);

  const totalFac   = activeRows.reduce((s, r) => s + r.fac,   0);
  const totalPray  = activeRows.reduce((s, r) => s + r.pray,  0);
  const totalUsher = activeRows.reduce((s, r) => s + r.usher, 0);
  const restingCount = allRows.filter(r => r.resting).length;

  const sorted = [...rows].sort((a, b) => {
    if (a.resting !== b.resting) return a.resting ? 1 : -1;
    return (b.fac + b.pray + b.usher) - (a.fac + a.pray + a.usher);
  });

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            📊 배정 횟수 통계 <span className="badge">{year}년</span>
            <span className="badge" style={{ background: '#6d28d9' }}>{rangeLabel}</span>
          </div>
          {restingCount > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer', color: 'var(--text-muted)' }}>
              <input type="checkbox" checked={showResting} onChange={e => setShowResting(e.target.checked)} />
              휴식 중 인원 포함 ({restingCount}명)
            </label>
          )}
        </div>

        {/* 기간 설정 */}
        <div style={{ background: '#f8fafc', border: '1.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
          {/* 프리셋 */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', alignSelf: 'center', marginRight: 2 }}>빠른 선택</span>
            {PRESETS.map(p => (
              <button key={p.label} onClick={() => applyPreset(p)} style={{
                padding: '4px 12px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', borderRadius: 6,
                border: '1.5px solid',
                borderColor: isPreset(p) ? 'var(--primary)' : 'var(--border)',
                background: isPreset(p) ? 'var(--primary)' : '#fff',
                color: isPreset(p) ? '#fff' : 'var(--text)',
                transition: 'all 0.12s',
              }}>{p.label}</button>
            ))}
          </div>
          {/* 직접 선택 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>직접 선택</span>
            <select className="form-control" style={{ width: 'auto' }} value={startMonth} onChange={e => handleStartChange(e.target.value)}>
              {MONTH_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}
            </select>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>부터</span>
            <select className="form-control" style={{ width: 'auto' }} value={endMonth} onChange={e => handleEndChange(e.target.value)}>
              {MONTH_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}
            </select>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>까지</span>
            <span style={{ fontSize: '0.82rem', color: 'var(--primary)', fontWeight: 700 }}>
              ({endMonth - startMonth + 1}개월 · 세션 {filtered.length}회)
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: '총 사회 배정', value: totalFac,   color: 'var(--primary)', bg: '#ede9fe' },
            { label: '총 기도 배정', value: totalPray,  color: '#7c3aed',        bg: '#f5f3ff' },
            { label: '총 안내 배정', value: totalUsher, color: 'var(--success)', bg: '#f0fdf4' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} style={{ flex: 1, minWidth: 120, background: bg, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: '0.75rem', color, fontWeight: 700, marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color }}>{value}<span style={{ fontSize: '0.85rem', marginLeft: 4 }}>회</span></div>
            </div>
          ))}
        </div>

        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          <span style={{ color: '#059669', fontWeight: 700 }}>▼ 초록(최소)</span>
          {' · '}
          <span style={{ color: '#dc2626', fontWeight: 700 }}>▲ 빨강(최대)</span>
          {' — 활동 중인 인원 기준. 같은 사람이 여러 역할을 가질 경우 역할별로 누적됩니다.'}
        </div>

        {sorted.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📊</div><div>배정 데이터가 없습니다.</div></div>
        ) : (
          <table className="schedule-table">
            <thead>
              <tr>
                <th>이름</th>
                <th style={{ textAlign: 'center' }}>상태</th>
                <th style={{ textAlign: 'center' }}>🎤 사회</th>
                <th style={{ textAlign: 'center' }}>🙏 기도</th>
                <th style={{ textAlign: 'center' }}>🚪 안내</th>
                <th style={{ textAlign: 'center' }}>합계</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.name} style={{ opacity: r.resting ? 0.6 : 1 }}>
                  <td style={{ fontWeight: 600, color: r.resting ? '#94a3b8' : 'var(--text)' }}>{r.name}</td>
                  <td style={{ textAlign: 'center' }}>
                    {r.resting
                      ? <span style={{ fontSize: '0.72rem', background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}>휴식 중</span>
                      : <span style={{ fontSize: '0.72rem', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}>활동 중</span>
                    }
                  </td>
                  <CountCell value={r.fac}   min={facMM.min}   max={facMM.max}   resting={r.resting} />
                  <CountCell value={r.pray}  min={prayMM.min}  max={prayMM.max}  resting={r.resting} />
                  <CountCell value={r.usher} min={usherMM.min} max={usherMM.max} resting={r.resting} />
                  <td style={{ textAlign: 'center', fontWeight: 700, color: r.resting ? '#94a3b8' : 'var(--text-muted)' }}>
                    {r.fac + r.pray + r.usher}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
